import React from 'react';

import Spinner from '@/src/components/common/Elements/Spinner/Spinner';
import NotAvailable from '@/src/components/notAvailable';
import { BASE_HASH_PATH, generateRoutePath } from '@/src/routes';

import FileEntryViewer from './FileEntryViewer/FileEntryViewer';
import Header from './Header/Header';

import { useEmailFileDetails } from '../../../components/EntryPanel/EmailThreadEntryViewer/components/EmailFileEntryViewer/api/getEmailFileDetails';
import useEmailFileEntryViewerStore from '../../../components/EntryPanel/EmailThreadEntryViewer/components/EmailFileEntryViewer/useEmailFileEntryViewerStore';
import { useMappeEmailEntryFile } from '../../../components/EntryPanel/EmailThreadEntryViewer/components/EmailFileEntryViewer/useMappedEmailEntryFile';
import { FileEntryViewerInfoPanelProvider } from '../../../components/EntryPanel/FileEntryViewer/store/fileEntryViewer.infoPanel.store';
import { FileEntryViewerSidebarActionsProvider } from '../../../components/EntryPanel/FileEntryViewer/store/fileEntryViewer.sidebarActions.store';
import { FileEntryViewerProvider } from '../../../components/EntryPanel/FileEntryViewer/store/fileEntryViewer.store';
import { INBOX_ENTRY_VIEWER_PAGES_PATH } from '../../../Config/inbox.routes';
import { FileEntry, InboxEntrySource, InboxEntryType } from '../../../types';
import { useProjectDetails } from '../../useProject';

export const getEmailFileViewerPageUrl = ({
  projectId,
  inboxEntryId,
  emailId,
  fileId,
}: {
  projectId: string;
  inboxEntryId: number;
  emailId: string;
  fileId: string;
}) => {
  return (
    window.location.origin +
    BASE_HASH_PATH +
    generateRoutePath(
      INBOX_ENTRY_VIEWER_PAGES_PATH,
      {
        projectId,
        inboxEntryId,
      },
      {
        type: InboxEntryType.DOCUMENT.toLowerCase(),
        source: InboxEntrySource.Email,
        emailId,
        fileId,
      }
    )
  );
};

type EmailFileEntryViewerPageProps = {
  mappedFileEntry: FileEntry;
  fileDetailsMutation: ReturnType<typeof useEmailFileDetails>;
  fileId: number;
  emailId: number;
  inboxEntryId: number;
  projectId: string;
};

const EmailFileEntryViewerPage = ({
  mappedFileEntry,
  fileId,
  inboxEntryId,
  emailId,
  projectId,
}: EmailFileEntryViewerPageProps) => {
  const { project } = useProjectDetails();

  const { sidebarActions, infoPanelQueries } = useEmailFileEntryViewerStore({
    projectId,
    emailId,
    inboxEntryId,
    fileId,
    documentFileId: mappedFileEntry.file.id,
  });

  if (!project) return <Spinner />;

  return (
    <FileEntryViewerInfoPanelProvider infoPanelQueries={infoPanelQueries}>
      <FileEntryViewerSidebarActionsProvider sidebarActions={sidebarActions}>
        <FileEntryViewerProvider>
          <FileEntryViewer
            header={
              <Header
                fileEntry={mappedFileEntry}
                projectName={project.project}
              />
            }
            fileEntry={mappedFileEntry}
          />
        </FileEntryViewerProvider>
      </FileEntryViewerSidebarActionsProvider>
    </FileEntryViewerInfoPanelProvider>
  );
};

type WrapperProps = {
  fileId: number;
  emailId: number;
  inboxEntryId: number;
  projectId: string;
};

const EmailFileEntryViewerPageWrapper = ({
  fileId,
  emailId,
  inboxEntryId,
  projectId,
}: WrapperProps) => {
  const { isLoading, mappedFileEntry, fileDetailsMutation } = useMappeEmailEntryFile({
    emailId,
    fileId,
    inboxEntryId,
    projectId,
  });

  if (!fileDetailsMutation && !isLoading) {
    return <NotAvailable />;
  }

  if (!mappedFileEntry) {
    return <Spinner />;
  }

  return (
    <EmailFileEntryViewerPage
      mappedFileEntry={mappedFileEntry}
      fileDetailsMutation={fileDetailsMutation}
      fileId={fileId}
      inboxEntryId={inboxEntryId}
      emailId={emailId}
      projectId={projectId}
    />
  );
};

export default EmailFileEntryViewerPageWrapper;
